import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import API from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tray, ArrowClockwise, Envelope, Archive, CheckCircle, ArrowBendUpLeft, PlugsConnected, Plugs } from "@phosphor-icons/react";
import { toast } from "sonner";
import OwnerReplyComposer from "./OwnerReplyComposer";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never");
const KIND = { chase: { label: "Chase reply", cls: "border-red-400 text-red-700" }, winback: { label: "Win-back reply", cls: "border-emerald-400 text-emerald-700" } };

function StatusPill({ st }) {
  if (!st?.configured) return <Badge variant="outline" className="text-[10px] gap-1 border-amber-400 text-amber-700" data-testid="owner-replies-status"><Plugs size={10} /> Mailbox not connected</Badge>;
  if (st.last_error) return <Badge variant="outline" className="text-[10px] gap-1 border-red-400 text-red-700" title={st.last_error} data-testid="owner-replies-status"><Plugs size={10} /> {st.mailbox} · last poll failed</Badge>;
  return <Badge variant="outline" className="text-[10px] gap-1 border-emerald-400 text-emerald-700" data-testid="owner-replies-status"><PlugsConnected size={10} /> {st.mailbox} · checked {fmtWhen(st.last_ok_at)}</Badge>;
}

function SentThread({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="mt-1.5 space-y-1" data-testid="owner-reply-thread">
      {items.map((s) => (
        <li key={s.id} className="rounded border border-emerald-200 bg-emerald-50/60 px-2 py-1.5 text-[11px]">
          <p className="text-emerald-800"><ArrowBendUpLeft size={10} className="inline mr-1 -mt-0.5" />Replied by {s.by_name || s.by} · {fmtWhen(s.at)} · from {s.from}</p>
          <p className="text-muted-foreground whitespace-pre-line line-clamp-3">{s.text}</p>
        </li>
      ))}
    </ul>
  );
}

function ReplyItem({ r, smtp, onRead, onArchive, onSent, focused = false }) {
  const [openBody, setOpenBody] = useState(focused);
  const [composing, setComposing] = useState(false);
  useEffect(() => { if (focused) setOpenBody(true); }, [focused]);
  const kind = KIND[r.outreach_kind];
  return (
    <li id={`owner-reply-${r.id}`} className={`px-4 py-2.5 text-xs transition-shadow ${r.read ? "" : "bg-primary/5"} ${focused ? "ring-2 ring-primary/60 ring-inset rounded-sm" : ""}`} data-testid={`owner-reply-${r.id}`} data-focused={focused ? "true" : undefined}>
      <div className="flex flex-wrap items-center gap-2">
        {!r.read && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" data-testid={`owner-reply-unread-${r.id}`} />}
        {r.company_name ? <Link to="/platform/tenants" className="font-semibold hover:underline">{r.company_name}</Link> : <span className="font-semibold text-muted-foreground" data-testid={`owner-reply-unmatched-${r.id}`}>Unmatched sender</span>}
        <span className="text-muted-foreground">{r.from_name ? `${r.from_name} · ` : ""}{r.from_email}</span>
        {kind && <Badge variant="outline" className={`text-[9px] ${kind.cls}`} data-testid={`owner-reply-kind-${r.id}`}>{kind.label}</Badge>}
        {r.replies_sent?.length > 0 && <Badge variant="outline" className="text-[9px] border-emerald-400 text-emerald-700" data-testid={`owner-reply-answered-${r.id}`}>Answered</Badge>}
        <span className="ml-auto text-[10px] text-muted-foreground">{fmtWhen(r.received_at)}</span>
      </div>
      <button type="button" className="mt-1 text-left w-full" onClick={() => { setOpenBody((v) => !v); if (!r.read) onRead(r); }} data-testid={`owner-reply-open-${r.id}`}>
        <p className="font-medium truncate">{r.subject || "(no subject)"}</p>
        <p className={`text-muted-foreground whitespace-pre-line ${openBody ? "" : "line-clamp-2"}`} data-testid={`owner-reply-text-${r.id}`}>{r.text || "(empty message)"}</p>
      </button>
      <SentThread items={r.replies_sent} />
      <div className="mt-1.5 flex items-center gap-3 text-[11px]">
        {smtp?.configured
          ? <button type="button" className="text-primary hover:underline inline-flex items-center gap-1" onClick={() => setComposing((v) => !v)} data-testid={`owner-reply-respond-${r.id}`}><ArrowBendUpLeft size={11} /> Reply</button>
          : <a href={`mailto:${r.from_email}?subject=${encodeURIComponent(`Re: ${r.subject || ""}`)}`} className="text-primary hover:underline inline-flex items-center gap-1" title="Billing mailbox SMTP not connected — opens your mail app" data-testid={`owner-reply-respond-${r.id}`}><ArrowBendUpLeft size={11} /> Reply</a>}
        {!r.read && <button type="button" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" onClick={() => onRead(r)} data-testid={`owner-reply-read-${r.id}`}><CheckCircle size={11} /> Mark read</button>}
        <button type="button" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" onClick={() => onArchive(r)} data-testid={`owner-reply-archive-${r.id}`}><Archive size={11} /> Archive</button>
      </div>
      {composing && <OwnerReplyComposer reply={r} smtp={smtp} onClose={() => setComposing(false)} onSent={(updated) => { setComposing(false); onSent(updated); }} />}
    </li>
  );
}

const badgeChanged = () => window.dispatchEvent(new Event("fixli:owner-replies-changed"));

export default function OwnerReplyInbox() {
  const { hash } = useLocation();
  const [data, setData] = useState(null);
  const [polling, setPolling] = useState(false);
  const [extra, setExtra] = useState(null); // deep-linked reply that isn't in the list any more (archived / older)
  const focusId = hash.startsWith("#owner-reply-") ? hash.slice("#owner-reply-".length) : null;
  const load = useCallback(() => API.get("/platform/payment-recovery/inbox").then(({ data: d }) => setData(d)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  // Deep link from a notification: scroll the thread into view, expand it, and mark it read (it's being looked at) — once per hash
  const handledRef = useRef(null);
  useEffect(() => {
    if (!data) return;
    if (hash === "#owner-replies") {
      if (handledRef.current !== hash) { handledRef.current = hash; document.getElementById("owner-replies")?.scrollIntoView({ behavior: "smooth", block: "start" }); }
      return;
    }
    if (!focusId || handledRef.current === focusId) return;
    handledRef.current = focusId;
    const inList = (data.items || []).find((r) => r.id === focusId);
    if (!inList) {
      API.get(`/platform/payment-recovery/inbox/${focusId}`).then(({ data: r }) => setExtra(r)).catch(() => setExtra({ missing: true, id: focusId }));
    } else {
      setExtra(null);
      if (!inList.read) API.post(`/platform/payment-recovery/inbox/${focusId}/read`, { read: true }).then(() => { badgeChanged(); load(); }).catch(() => {});
    }
    setTimeout(() => document.getElementById(`owner-reply-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
  }, [hash, focusId, data, load]);
  useEffect(() => {
    if (extra && !extra.missing) setTimeout(() => document.getElementById(`owner-reply-${extra.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  }, [extra]);

  const poll = async () => {
    setPolling(true);
    try {
      const { data: r } = await API.post("/platform/payment-recovery/inbox/poll");
      toast.success(r.stored ? `${r.stored} new repl${r.stored === 1 ? "y" : "ies"} filed` : `Mailbox checked — nothing new${r.auto_replies ? ` (${r.auto_replies} auto-reply skipped)` : ""}`);
      load(); badgeChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't check the mailbox"); load(); } finally { setPolling(false); }
  };
  const markRead = async (r) => { try { await API.post(`/platform/payment-recovery/inbox/${r.id}/read`, { read: true }); load(); badgeChanged(); } catch { toast.error("Couldn't update"); } };
  const archive = async (r) => { try { await API.post(`/platform/payment-recovery/inbox/${r.id}/archive`); toast.success("Archived"); load(); badgeChanged(); } catch { toast.error("Couldn't archive"); } };

  const st = data?.status;
  const items = data?.items || [];
  return (
    <div id="owner-replies" className="border-t border-border" data-testid="owner-replies">
      <div className="px-4 py-2 bg-muted/20 flex flex-wrap items-center gap-2">
        <Tray size={13} className="text-muted-foreground" />
        <p className="text-[10px] font-semibold text-muted-foreground">OWNER REPLIES</p>
        {st?.unread > 0 && <Badge className="text-[9px] h-4 px-1.5" data-testid="owner-replies-unread">{st.unread} unread</Badge>}
        <StatusPill st={st} />
        <div className="ml-auto flex items-center gap-2">
          {st?.reply_to && <span className="text-[10px] text-muted-foreground" data-testid="owner-replies-reply-to">{st.smtp?.configured ? `Replies sent from ${st.smtp.from} via ${st.smtp.host}` : `Replies go to ${st.reply_to}`}</span>}
          <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={poll} disabled={polling || !st?.configured} data-testid="owner-replies-poll"><ArrowClockwise size={12} className={`mr-1 ${polling ? "animate-spin" : ""}`} /> {polling ? "Checking…" : "Check now"}</Button>
        </div>
      </div>
      {st && !st.configured && (
        <p className="px-4 py-2.5 text-xs text-muted-foreground" data-testid="owner-replies-setup">
          <Envelope size={12} className="inline mr-1 -mt-0.5" /> Connect the billing mailbox so replies to chase and win-back emails land here: set <code className="font-mono">BILLING_IMAP_HOST</code>, <code className="font-mono">BILLING_IMAP_USER</code>, <code className="font-mono">BILLING_IMAP_PASSWORD</code> (app password) and <code className="font-mono">BILLING_REPLY_TO</code> on the deployment. Outreach emails already carry a <code className="font-mono">Ref: FX-…</code> footer so replies match the right workspace.
        </p>
      )}
      {extra?.missing && <p className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100" data-testid="owner-reply-missing">That reply no longer exists.</p>}
      {extra && !extra.missing && (
        <ul className="divide-y divide-border border-b border-border" data-testid="owner-replies-deeplinked">
          <li className="px-4 pt-2 text-[10px] text-muted-foreground">{extra.archived ? "Archived thread (opened from a notification)" : "Opened from a notification"}</li>
          <ReplyItem r={extra} smtp={st?.smtp} onRead={markRead} onArchive={archive} onSent={load} focused />
        </ul>
      )}
      {st?.configured && items.length === 0 && !extra && <p className="px-4 py-2.5 text-xs text-muted-foreground" data-testid="owner-replies-empty">No replies yet — they'll appear here within 5 minutes of arriving.</p>}
      {items.length > 0 && <ul className="divide-y divide-border" data-testid="owner-replies-list">{items.map((r) => <ReplyItem key={r.id} r={r} smtp={st?.smtp} onRead={markRead} onArchive={archive} onSent={load} focused={r.id === focusId} />)}</ul>}
    </div>
  );
}
